/**
 * Utility functions for parsing paper data from various formats
 */

/**
 * Parse paper scores and reasoning from raw backend logs
 * @param {string} logData - Raw log data from backend
 * @returns {Array} - Array of paper objects with scores and reasoning
 */
export const parsePaperScoresFromLogs = (logData) => {
  let papers = [];

  if (!logData || typeof logData !== 'string') {
    return papers;
  }

  // First look for the debug output format (most reliable)
  try {
    const paperDebugRegex = /Parser Output \((\d+) papers found\):\s*(\[\s*\{[\s\S]*?\}\s*\])\s*/;
    const debugMatch = logData.match(paperDebugRegex);

    if (debugMatch && debugMatch[2]) {

      try {
        // Clean and parse the JSON string from debug output
        let jsonString = debugMatch[2]
          .replace(/\n/g, ' ')
          .replace(/'/g, '"')
          .replace(/([{,])\s*([a-zA-Z0-9_]+):/g, '$1"$2":'); // Add quotes to keys

        const parsedData = JSON.parse(jsonString);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          return parsedData;
        }
      } catch (jsonError) {
        console.error('Error parsing debug JSON:', jsonError);
      }
    }

    // Check for 'Extracted paper scores' section which matches your debug output
    const extractedScoresRegex = /Extracted paper scores:\s*(\[\s*\{[\s\S]*?\}\s*\])\s*/;
    const extractedMatch = logData.match(extractedScoresRegex);

    if (extractedMatch && extractedMatch[1]) {

      try {
        // Clean up the extracted string to make it valid JSON
        let jsonString = extractedMatch[1]
          .replace(/\n/g, ' ')
          .replace(/'/g, '"')
          .replace(/([{,])\s*([a-zA-Z0-9_]+):/g, '$1"$2":'); // Add quotes to keys

        const parsedData = JSON.parse(jsonString);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          return parsedData;
        }
      } catch (jsonError) {
        console.error('Something went wrong');
      }
    }

    // Fall back to other parsing strategies if needed
    // Additional parsing logic...

    // Legacy logic
    // Strategy 1: Look for Paper ID and Score patterns
    const scoreRegex = /Paper ID: (\d+)\s+Score: (\d+)/g;
    let match;
    while ((match = scoreRegex.exec(logData)) !== null) {
      const paperId = parseInt(match[1]);
      const score = parseInt(match[2]);

      // Check if paper already exists in array
      const existingPaperIndex = papers.findIndex(p => p.paperId === paperId);
      if (existingPaperIndex === -1) {
        papers.push({
          paperId,
          score
        });
      } else {
        papers[existingPaperIndex].score = score;
      }
    }

    // Extract paper titles if available
    const titleRegex = /Paper ID: (\d+)[\s\S]*?Title: ([^\n]+)/g;
    let titleMatch;
    while ((titleMatch = titleRegex.exec(logData)) !== null) {
      const paperId = parseInt(titleMatch[1]);
      const title = titleMatch[2].trim();

      const paperIndex = papers.findIndex(p => p.paperId === paperId);
      if (paperIndex !== -1) {
        papers[paperIndex].title = title;
      } else {
        // Create a new paper entry if it doesn't exist
        papers.push({
          paperId,
          title,
          score: 0
        });
      }
    }

    // Extract reasoning with regex
    const argumentsForRegex = /Paper ID: (\d+)[\s\S]*?Arguments For:\s*([\s\S]*?)(?=Arguments Against:|Paper ID:|$)/g;
    const argumentsAgainstRegex = /Paper ID: (\d+)[\s\S]*?Arguments Against:\s*([\s\S]*?)(?=Paper ID:|$)/g;

    let forMatch;
    while ((forMatch = argumentsForRegex.exec(logData)) !== null) {
      const paperId = parseInt(forMatch[1]);
      const args = forMatch[2].trim();

      const paperIndex = papers.findIndex(p => p.paperId === paperId);
      if (paperIndex !== -1) {
        papers[paperIndex].reasoning = papers[paperIndex].reasoning || {};
        papers[paperIndex].reasoning.argumentsFor = args;
      }
    }

    let againstMatch;
    while ((againstMatch = argumentsAgainstRegex.exec(logData)) !== null) {
      const paperId = parseInt(againstMatch[1]);
      const args = againstMatch[2].trim();

      const paperIndex = papers.findIndex(p => p.paperId === paperId);
      if (paperIndex !== -1) {
        papers[paperIndex].reasoning = papers[paperIndex].reasoning || {};
        papers[paperIndex].reasoning.argumentsAgainst = args;
      }
    }

    // Sort papers by score if available
    papers.sort((a, b) => (b.score || 0) - (a.score || 0));
  } catch (e) {
    console.error('Something went wrong');
  }

  return papers;
};

/**
 * Process ranking analysis data from the API response
 * @param {Array} rankingAnalysis - Ranking analysis data from API response
 * @returns {Array} - Processed paper data with scores and reasoning
 */
export const processRankingAnalysis = (rankingAnalysis) => {

  if (!rankingAnalysis || !Array.isArray(rankingAnalysis)) {
    console.warn('Invalid ranking analysis data:', rankingAnalysis);
    return [];
  }

  // Transform the ranking analysis data into a standardized format
  const processedResults = rankingAnalysis.map((paper, index) => {

    return {
      paperId: index, // Use index as paperId if not provided
      title: paper.title || `Paper ${index + 1}`,
      score: paper.score || 0,
      reasoning: {
        argumentsFor: paper.argumentsFor || '',
        argumentsAgainst: paper.argumentsAgainst || ''
      },
      relevantQuotes: paper.relevantQuotes || []
    };
  });

  return processedResults;
};

/**
 * Format raw paper data for display in the UI
 * @param {Array} papers - Array of paper objects
 * @returns {Array} - Array of formatted paper objects
 */
export const formatPapersForDisplay = (papers) => {
  if (!papers || !Array.isArray(papers)) {
    console.warn('Invalid papers data for formatting:', papers);
    return [];
  }

  // Format each paper for display
  const enhancedPapers = papers.map(paper => {
    // Start with the basic paper info
    const formatted = {
      paperId: paper.paperId !== undefined ? paper.paperId : 0,
      // Use a meaningful title or a placeholder
      title: paper.title || `Paper ${paper.paperId !== undefined ? paper.paperId : 0}`,
      // Normalize score to be consistent percentage
      score: formatScore(paper.score),
      reasoning: {
        argumentsFor: '',
        argumentsAgainst: ''
      },
      // Include relevant quotes if available
      relevantQuotes: paper.relevantQuotes || []
    };

    // Handle different reasoning formats
    if (paper.reasoning) {
      if (typeof paper.reasoning === 'object') {
        formatted.reasoning.argumentsFor = paper.reasoning.argumentsFor || '';
        formatted.reasoning.argumentsAgainst = paper.reasoning.argumentsAgainst || '';
      } else if (typeof paper.reasoning === 'string') {
        // Try to split string reasoning into for/against
        const parts = paper.reasoning.split('argumentsAgainst:');
        if (parts.length > 1) {
          formatted.reasoning.argumentsFor = parts[0].replace('argumentsFor:', '').trim();
          formatted.reasoning.argumentsAgainst = parts[1].trim();
        } else {
          formatted.reasoning.argumentsFor = paper.reasoning;
        }
      }
    }

    // Handle argumentsFor/Against directly on paper object 
    if (paper.argumentsFor) {
      formatted.reasoning.argumentsFor = paper.argumentsFor;
    }
    if (paper.argumentsAgainst) {
      formatted.reasoning.argumentsAgainst = paper.argumentsAgainst;
    }

    return formatted;
  });

  return enhancedPapers;
};

/**
 * Format score consistently as a percentage
 * @param {number|string} score - Raw score value
 * @returns {string} - Formatted percentage score with % symbol
 */
const formatScore = (score) => {
  if (score === undefined || score === null) return '0%';

  // Convert to number if it's a string
  let numScore = typeof score === 'string' ? parseFloat(score) : score;

  // If score is already a percentage (0-100)
  if (numScore > 1) {
    return `${Math.round(numScore)}%`;
  }

  // If score is a decimal (0-1), convert to percentage
  return `${Math.round(numScore * 100)}%`;
};
